import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Btn,
  Cta,
  Footer,
  Icon,
  ItemRow,
  JobTile,
  Keypad,
  Readout,
  Row,
  Strip,
  V1Screen,
  WaitCta,
} from '@/features/gate/v1/kit'
import { Refused } from '@/features/gate/v1/scan'
import { C, typeKey } from '@/features/gate/v1/style'
import { cropWord, shortLot, tr } from '@/features/gate/v1/text'
import type { ProcessState } from '@/features/process/projection'
import { SavedLine } from '@/features/process/v1/ui'
import type { PackLot } from '@/offline/sitePack'
import { pouchesLeft } from '../model/reconcile'
import { buyersOf, type Buyer } from './hallData'
import { dispatchV1 } from './hallApi'
import { dispatchable, hallTotals, invoiceOk, tripLines } from './model'
import { DoneScreen, Figure, Lead, Locked, PackHeader, WithHall, YesSheet } from './ui'
import { callFailed, packSize } from './words'
import { useHall } from './useHall'
import { HeldRunsTile, LotsTile } from './Lots'

const num = (n: number) => n.toLocaleString('en-IN')

/** "मूंगफली · 500 ग्राम" under a lot. */
function lotLine(l: PackLot): string {
  return [cropWord(l.sku_name), l.net_g ? packSize(l.net_g) : null].filter(Boolean).join(' · ')
}

/** V1-F7-08a — पैकिंग हॉल: pouches at the hall and the lots that can go. */
export function V1HallHome() {
  return <WithHall>{(state) => <HallHome state={state} />}</WithHall>
}

function HallHome({ state }: { state: ProcessState }) {
  const navigate = useNavigate()
  const hall = useHall(state, { buyers: true })
  const lots = [...state.packLots.values()]
  const t = hallTotals(lots)
  const ready = dispatchable(lots)
  return (
    <V1Screen
      nav
      header={
        <PackHeader
          icon="home"
          title={tr('pack.v1.hall.title')}
          sub={state.pack.site_code ?? state.pack.site_name ?? undefined}
        />
      }
      footer={
        <Footer>
          {ready.length > 0 ? (
            <Cta
              label={tr('pack.v1.hall.dispatch')}
              icon="truck"
              onClick={() => navigate('/m/pack/dispatch')}
            />
          ) : (
            <WaitCta>{tr('pack.v1.hall.noneReady')}</WaitCta>
          )}
        </Footer>
      }
    >
      <h2 style={{ margin: '2px 0 0', fontSize: 18, fontWeight: 700 }}>
        {tr('pack.v1.hall.atHall')}
      </h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 6 }}>
        <Figure value={num(t.released)} label={tr('pack.v1.hall.released')} />
        <Figure value={num(t.waiting)} label={tr('pack.v1.hall.waiting')} />
        <Figure value={num(t.held)} label={tr('pack.v1.hall.held')} />
      </div>
      <h2 style={{ margin: '4px 0 0', fontSize: 18, fontWeight: 700 }}>
        {tr('pack.v1.hall.canGo')}
      </h2>
      {ready.length === 0 && <Strip icon="hourglass">{tr('pack.v1.hall.noneReadyLine')}</Strip>}
      {ready.map((l) => (
        <ItemRow
          key={l.id}
          padding="8px 10px"
          lead={<Lead icon="pouch" bg={C.okWash} color={C.ok} />}
          title={tr('pack.v1.lotTail', { tail: shortLot(l.code) })}
          line={<span style={{ fontSize: 15, color: C.muted }}>{lotLine(l)}</span>}
          right={<b style={{ fontSize: 22 }}>{num(pouchesLeft(l))}</b>}
        />
      ))}
      <JobTile
        icon="truck"
        color={C.acc}
        title={tr('pack.v1.hall.receive')}
        line={tr('pack.v1.hall.receiveLine')}
        onClick={() => navigate('/m/store')}
      />
      <HeldRunsTile state={state} />
      <LotsTile />
      {hall.online && (
        <Row>
          <Btn icon="download" disabled={hall.busy} onClick={hall.refresh}>
            {tr('pack.v1.home.refresh')}
          </Btn>
        </Row>
      )}
    </V1Screen>
  )
}

// ---------------------------------------------------------------------------------------------
// F7.8 — माल भेजें: customer, invoice, lots and pouches, confirm
// ---------------------------------------------------------------------------------------------

type Step = 'buyer' | 'invoice' | 'lots'

/** V1-F7-08b … 08j — one trip to a customer. */
export function V1Dispatch() {
  return <WithHall>{(state) => <Dispatch state={state} />}</WithHall>
}

function Dispatch({ state }: { state: ProcessState }) {
  const navigate = useNavigate()
  const hall = useHall(state, { buyers: true })
  const buyers = buyersOf(state.pack)
  const lots = dispatchable([...state.packLots.values()])
  const [step, setStep] = useState<Step>('buyer')
  const [buyer, setBuyer] = useState<Buyer | null>(null)
  const [invoice, setInvoice] = useState('')
  const [typed, setTyped] = useState<Record<string, number>>({})
  const [picked, setActive] = useState<string | null>(null)
  // The lot the keypad types into: the one tapped, else the first.
  const active = picked ?? lots[0]?.id ?? null
  const [sheet, setSheet] = useState(false)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [done, setDone] = useState<{ buyer: Buyer; invoice: string; pouches: number } | null>(null)
  const onInvoiceKey = useCallback(
    (k: string) => setInvoice((c) => typeKey(c, k, { decimals: 0, max: 8 })),
    [],
  )
  const onPouchKey = (k: string) => {
    if (!active) return
    setErr(null)
    setTyped((t) => {
      const cur = t[active] ? String(t[active]) : ''
      const next = typeKey(cur, k, { decimals: 0, max: 6 })
      return { ...t, [active]: next ? Number(next) : 0 }
    })
  }
  const home = () => navigate('/m/pack/hall')
  const where = (b: Buyer) => [b.name, b.place].filter(Boolean).join(' · ')

  if (done)
    return (
      <DoneScreen
        title={tr('pack.v1.sent.title')}
        lines={[
          tr('pack.v1.sent.line', { buyer: done.buyer.name, invoice: done.invoice }),
          tr('pack.v1.pouches', { count: done.pouches }),
        ]}
        todo={[tr('pack.v1.sent.s1')]}
        cta={<Cta label={tr('pack.v1.home')} icon="home" lead onClick={home} />}
      />
    )

  // F7.8 — किसे भेजना है?
  if (step === 'buyer' || !buyer)
    return (
      <V1Screen
        header={
          <PackHeader
            onBack={home}
            icon="store"
            title={tr('pack.v1.buyer.title')}
            step={1}
            of={3}
          />
        }
        footer={
          <Footer>
            {buyer ? (
              <Cta label={tr('pack.v1.next')} icon="next" onClick={() => setStep('invoice')} />
            ) : (
              <WaitCta>{tr('pack.v1.buyer.wait')}</WaitCta>
            )}
          </Footer>
        }
      >
        {buyers.length === 0 && (
          <Strip icon="bang">
            {hall.online ? tr('pack.v1.buyer.none') : tr('pack.v1.needsNet')}
          </Strip>
        )}
        {buyers.map((b) => {
          const on = buyer?.id === b.id
          return (
            <ItemRow
              key={b.id}
              padding="8px 10px"
              onClick={() => setBuyer(b)}
              border={on ? C.ink : C.border}
              borderWidth={on ? 3 : 1.5}
              lead={<Lead icon="store" />}
              title={<span style={{ fontSize: 19 }}>{b.name}</span>}
              line={
                b.place ? (
                  <span style={{ fontSize: 15, color: C.muted }}>{b.place}</span>
                ) : undefined
              }
              right={
                on ? (
                  <span
                    aria-hidden="true"
                    style={{
                      width: 26,
                      height: 26,
                      borderRadius: 13,
                      background: C.ink,
                      color: '#ffffff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Icon name="ok" size={16} sw={3} />
                  </span>
                ) : undefined
              }
            />
          )
        })}
      </V1Screen>
    )

  // F7.8 — इनवॉइस नंबर
  if (step === 'invoice')
    return (
      <V1Screen
        main={{ padding: '8px 12px', gap: 8 }}
        header={
          <PackHeader
            onBack={() => setStep('buyer')}
            icon="file"
            title={tr('pack.v1.invoice.title')}
            step={2}
            of={3}
          />
        }
        footer={
          <Footer dense>
            {invoiceOk(invoice) ? (
              <Cta label={tr('pack.v1.next')} icon="next" onClick={() => setStep('lots')} />
            ) : (
              <WaitCta>{tr('pack.v1.invoice.wait')}</WaitCta>
            )}
          </Footer>
        }
      >
        <SavedLine>{where(buyer)}</SavedLine>
        <Readout
          label={tr('pack.v1.invoice.label')}
          value={invoice}
          size={44}
          padding="4px 12px 6px"
          labelSize={16}
        />
        <div style={{ flexGrow: 1 }} />
        <Keypad dot={false} onKey={onInvoiceKey} />
      </V1Screen>
    )

  // F7.8 — कितने पाउच?
  const trip = tripLines(lots, typed)
  const total = trip.lines.reduce((a, l) => a + l.pouches, 0)
  const send = async () => {
    setSaving(true)
    setErr(null)
    try {
      await dispatchV1({
        from_location_id: state.siteId,
        to_org_id: buyer.id,
        invoice_no: invoice,
        items: trip.lines.map((l) => ({
          pack_lot_id: l.lot.id,
          pack_lot_code: l.lot.code,
          pouches: l.pouches,
        })),
      })
      setDone({ buyer, invoice, pouches: total })
    } catch (e) {
      setErr(callFailed(e, tr('pack.v1.sent.failed')))
      setSheet(false)
    } finally {
      setSaving(false)
    }
  }
  return (
    <V1Screen
      main={{ padding: '8px 12px', gap: 6 }}
      header={
        <PackHeader
          onBack={() => setStep('invoice')}
          icon="pouch"
          title={tr('pack.v1.lots.title')}
          step={3}
          of={3}
        />
      }
      footer={
        <Footer dense>
          {err && <Refused text={err} />}
          {trip.over ? (
            <WaitCta>
              {tr('pack.v1.lots.over', {
                tail: shortLot(trip.over.code),
                left: pouchesLeft(trip.over),
              })}
            </WaitCta>
          ) : trip.lines.length === 0 ? (
            <WaitCta>{tr('pack.v1.lots.wait')}</WaitCta>
          ) : hall.online ? (
            <Cta label={tr('pack.v1.next')} icon="next" onClick={() => setSheet(true)} />
          ) : (
            <WaitCta>{tr('pack.v1.needsNet')}</WaitCta>
          )}
        </Footer>
      }
      overlay={
        sheet ? (
          <YesSheet
            title={tr('pack.v1.trip.title')}
            yes={tr('pack.v1.trip.yes')}
            yesIcon="truck"
            busy={saving}
            onYes={() => void send()}
            onClose={() => setSheet(false)}
            top={200}
          >
            <ItemRow
              padding="8px 10px"
              lead={<Lead icon="store" />}
              title={where(buyer)}
              line={tr('pack.v1.trip.line', { invoice, count: total })}
            />
            <section
              style={{
                background: C.surface,
                border: `1.5px solid ${C.rule}`,
                borderRadius: 12,
                padding: '8px 12px',
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
              }}
            >
              {trip.lines.map((l) => (
                <div
                  key={l.lot.id}
                  style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 17 }}
                >
                  <span>{tr('pack.v1.lotTail', { tail: shortLot(l.lot.code) })}</span>
                  <b>{tr('pack.v1.pouches', { count: l.pouches })}</b>
                </div>
              ))}
            </section>
            <Locked>{tr('pack.v1.trip.locked')}</Locked>
          </YesSheet>
        ) : undefined
      }
    >
      {lots.length === 0 && <Strip icon="hourglass">{tr('pack.v1.hall.noneReadyLine')}</Strip>}
      {lots.map((l) => {
        const on = active === l.id
        const n = typed[l.id] ?? 0
        return (
          <ItemRow
            key={l.id}
            padding="6px 10px"
            onClick={() => setActive(l.id)}
            border={on ? C.acc : C.rule}
            borderWidth={on ? 2 : 1.5}
            lead={<Lead icon="pouch" bg={C.okWash} color={C.ok} />}
            title={tr('pack.v1.lotTail', { tail: shortLot(l.code) })}
            line={
              <span style={{ fontSize: 15, color: C.muted }}>
                {tr('pack.v1.lots.left', { count: pouchesLeft(l) })}
              </span>
            }
            right={
              <span
                style={{
                  minWidth: 72,
                  height: 44,
                  padding: '0 8px',
                  boxSizing: 'border-box',
                  borderRadius: 10,
                  border: on ? `2px solid ${C.acc}` : `1.5px solid ${C.border}`,
                  background: C.surface,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: 2,
                }}
              >
                {n > 0 ? (
                  <b style={{ fontSize: 24 }}>{n}</b>
                ) : (
                  <span style={{ fontSize: 20, color: C.muted }}>—</span>
                )}
                {on && (
                  <span aria-hidden="true" style={{ width: 3, height: 26, background: C.acc }} />
                )}
              </span>
            }
          />
        )
      })}
      <div style={{ flexGrow: 1 }} />
      {lots.length > 0 && <Keypad dot={false} onKey={onPouchKey} />}
    </V1Screen>
  )
}
